// @ts-nocheck
/**
 * NPCトレードシステム - コアロジック
 *
 * ゼルヴァンの基本トレード、オファー生成、予約システム
 */

import {
  OFFER_TYPE_RATES,
  GIVE_TYPE_RATES,
  VALUE_RATIO_RANGES,
  GAMBLE_RESULTS,
  RESERVATION_CONFIG,
  OFFER_CONFIG,
  CONDITION_PATTERNS,
  RARITY_ORDER,
  ATTRIBUTES,
} from './tradeConstants';

// ========================================
// ユーティリティ関数
// ========================================

/**
 * 確率に基づいて抽選
 * @param {Object} rates - { key: probability }
 * @returns {string} 選ばれたkey
 */
const drawFromRates = (rates) => {
  const rand = Math.random();
  let cumulative = 0;

  for (const [key, rate] of Object.entries(rates)) {
    cumulative += rate;
    if (rand < cumulative) {
      return key;
    }
  }

  return Object.keys(rates)[0];
};

/**
 * 範囲内でランダムな値を生成
 * @param {number} min
 * @param {number} max
 * @returns {number}
 */
const randomInRange = (min, max) => {
  return min + Math.random() * (max - min);
};

/**
 * 配列をシャッフル
 * @param {Array} array
 * @returns {Array}
 */
const shuffleArray = (array) => {
  const result = [...array];
  for (let i = result.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
};

/**
 * ユニークIDを生成
 * @returns {string}
 */
const generateOfferId = () => {
  return `offer_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * レアリティのインデックスを取得
 * @param {string} rarity
 * @returns {number}
 */
const getRarityIndex = (rarity) => {
  return RARITY_ORDER.indexOf(rarity);
};

// ========================================
// カード価値計算
// ========================================

/**
 * カードの市場価値を取得
 * @param {Object} card - カードデータ
 * @param {string} rarity - レアリティ
 * @param {Function} getMarketPrice - 市場価格取得関数
 * @returns {number}
 */
export const getCardValue = (card, rarity, getMarketPrice) => {
  if (getMarketPrice) {
    return getMarketPrice(card, rarity);
  }
  // フォールバック: レアリティベースの概算価格
  const rarityIndex = getRarityIndex(rarity);
  const basePrice = 100;
  return basePrice * Math.pow(3, rarityIndex);
};

/**
 * オファーの総価値を計算
 * @param {Array} cards - カードリスト [{ cardId, count, rarity }]
 * @param {Array} allCards - 全カードデータ
 * @param {Function} getMarketPrice - 市場価格取得関数
 * @returns {number}
 */
export const calculateOfferValue = (cards, allCards, getMarketPrice) => {
  let total = 0;
  for (const item of cards) {
    const card = allCards.find(c => c.id === item.cardId);
    if (card) {
      const value = getCardValue(card, item.rarity, getMarketPrice);
      total += value * (item.count || 1);
    }
  }
  return total;
};

// ========================================
// 条件マッチング
// ========================================

/**
 * カードが条件にマッチするかチェック
 * @param {Object} card - カードデータ
 * @param {string} rarity - 所持レアリティ
 * @param {Object} condition - 条件オブジェクト
 * @returns {boolean}
 */
export const checkConditionMatch = (card, rarity, condition) => {
  if (!condition) return true;

  // 属性チェック
  if (condition.attribute && card.attribute !== condition.attribute) {
    return false;
  }

  // レアリティチェック
  if (condition.rarity && rarity !== condition.rarity) {
    return false;
  }

  // カテゴリチェック
  if (condition.category) {
    const categoryStr = typeof card.category === 'string' ? card.category : '';
    if (!categoryStr || !categoryStr.includes(condition.category)) {
      return false;
    }
  }

  // コスト上限チェック
  if (condition.maxCost !== undefined && card.cost > condition.maxCost) {
    return false;
  }

  return true;
};

/**
 * プレイヤーのコレクションから条件にマッチするカードを取得
 * @param {Array} collection - プレイヤーのコレクション
 * @param {Array} allCards - 全カードデータ
 * @param {Object} condition - 条件オブジェクト
 * @returns {Array} マッチするカード [{ cardId, rarity, quantity, card }]
 */
export const getMatchingCards = (collection, allCards, condition) => {
  const result = [];

  for (const item of collection) {
    const card = allCards.find(c => c.id === item.cardId);
    if (!card) continue;

    if (checkConditionMatch(card, item.rarity, condition)) {
      result.push({
        cardId: item.cardId,
        rarity: item.rarity,
        quantity: item.quantity || 1,
        card,
      });
    }
  }

  return result;
};

/**
 * 条件にマッチするカードの総枚数を取得
 * @param {Array} collection - プレイヤーのコレクション
 * @param {Array} allCards - 全カードデータ
 * @param {Object} condition - 条件オブジェクト
 * @returns {number}
 */
export const countMatchingCards = (collection, allCards, condition) => {
  const matching = getMatchingCards(collection, allCards, condition);
  return matching.reduce((sum, item) => sum + item.quantity, 0);
};

// ========================================
// 「渡すカード」生成
// ========================================

/**
 * 具体的指定の「渡すカード」を生成
 * @param {Array} collection - プレイヤーのコレクション
 * @param {Array} allCards - 全カードデータ
 * @param {number} targetValue - 目標価値
 * @param {Function} getMarketPrice - 市場価格取得関数
 * @returns {Array | null} [{ cardId, count, rarity }]
 */
export const generateSpecificGive = (collection, allCards, targetValue, getMarketPrice) => {
  // 所持カードをシャッフルして選択
  const availableCards = collection
    .filter(item => (item.quantity || 1) >= 1)
    .map(item => {
      const card = allCards.find(c => c.id === item.cardId);
      if (!card) return null;
      return {
        cardId: item.cardId,
        rarity: item.rarity,
        quantity: item.quantity || 1,
        card,
        value: getCardValue(card, item.rarity, getMarketPrice),
      };
    })
    .filter(Boolean);

  if (availableCards.length === 0) return null;

  const shuffled = shuffleArray(availableCards);
  const selected = [];
  let currentValue = 0;
  let totalCount = 0;

  // 目標価値に近づくようにカードを選択
  for (const item of shuffled) {
    if (totalCount >= OFFER_CONFIG.maxCardsPerOffer) break;
    if (currentValue >= targetValue * 0.8) break;

    const maxCount = Math.min(
      item.quantity,
      OFFER_CONFIG.maxCardsPerOffer - totalCount,
      Math.ceil((targetValue - currentValue) / item.value)
    );

    if (maxCount >= 1) {
      const count = Math.max(1, Math.floor(Math.random() * maxCount) + 1);
      selected.push({
        cardId: item.cardId,
        rarity: item.rarity,
        count,
      });
      currentValue += item.value * count;
      totalCount += count;
    }
  }

  if (selected.length === 0) return null;

  return selected;
};

/**
 * 条件指定の「渡すカード」を生成
 * @param {Array} collection - プレイヤーのコレクション
 * @param {Array} allCards - 全カードデータ
 * @param {number} targetValue - 目標価値
 * @param {Function} getMarketPrice - 市場価格取得関数
 * @returns {{ condition: Object, count: number } | null}
 */
export const generateConditionGive = (collection, allCards, targetValue, getMarketPrice) => {
  // 条件パターンを選択
  const patternRates = {};
  for (const pattern of CONDITION_PATTERNS) {
    patternRates[pattern.type] = pattern.weight;
  }
  const patternType = drawFromRates(patternRates);

  let condition = {};

  switch (patternType) {
    case 'attribute': {
      const attr = ATTRIBUTES[Math.floor(Math.random() * ATTRIBUTES.length)];
      const rarities = ['C', 'UC', 'R'];
      const rarity = rarities[Math.floor(Math.random() * rarities.length)];
      condition = { attribute: attr, rarity };
      break;
    }
    case 'rarity': {
      const rarities = ['C', 'UC', 'R'];
      const rarity = rarities[Math.floor(Math.random() * rarities.length)];
      condition = { rarity };
      break;
    }
    case 'category': {
      // カテゴリはコレクションから存在するものを選ぶ
      const categories = new Set();
      for (const item of collection) {
        const card = allCards.find(c => c.id === item.cardId);
        if (card?.category) {
          // categoryが文字列の場合のみmatchを使用
          const categoryStr = typeof card.category === 'string' ? card.category : '';
          const matches = categoryStr.match(/【([^】]+)】/g) || [];
          for (const match of matches) {
            categories.add(match.replace(/[【】]/g, ''));
          }
        }
      }
      if (categories.size === 0) {
        condition = { rarity: 'UC' };
      } else {
        const categoryArray = Array.from(categories);
        const cat = categoryArray[Math.floor(Math.random() * categoryArray.length)];
        condition = { category: cat, rarity: 'UC' };
      }
      break;
    }
    case 'any':
    default: {
      const rarities = ['C', 'UC', 'R'];
      const rarity = rarities[Math.floor(Math.random() * rarities.length)];
      condition = { rarity };
      break;
    }
  }

  // 条件にマッチするカードの枚数をチェック
  const matchCount = countMatchingCards(collection, allCards, condition);
  if (matchCount === 0) return null;

  // 平均価値を計算
  const matchingCards = getMatchingCards(collection, allCards, condition);
  const avgValue = matchingCards.reduce((sum, item) => {
    return sum + getCardValue(item.card, item.rarity, getMarketPrice);
  }, 0) / matchingCards.length;

  // 必要枚数を計算
  let count = Math.max(1, Math.round(targetValue / avgValue));
  count = Math.min(count, matchCount, OFFER_CONFIG.maxCardsPerOffer);

  if (count === 0) return null;

  return { condition, count };
};

// ========================================
// 「もらうカード」生成
// ========================================

/**
 * 「もらうカード」を生成
 * @param {Array} allCards - 全カードデータ
 * @param {number} targetValue - 目標価値
 * @param {Function} getMarketPrice - 市場価格取得関数
 * @param {Object} options - オプション { offerType, excludeAttribute }
 * @returns {Array} [{ cardId, count, rarity }]
 */
export const generateReceiveCards = (allCards, targetValue, getMarketPrice, options = {}) => {
  const { offerType, excludeAttribute } = options;

  // フィルタリング
  let filteredCards = allCards.filter(card => card.type === 'monster' || card.type === 'magic');

  // 属性変換の場合、元の属性を除外
  if (offerType === 'attribute' && excludeAttribute) {
    filteredCards = filteredCards.filter(card => card.attribute !== excludeAttribute);
  }

  const shuffled = shuffleArray(filteredCards);
  const selected = [];
  let currentValue = 0;
  let totalCount = 0;

  // 目標価値に近づくようにカードを選択
  for (const card of shuffled) {
    if (totalCount >= OFFER_CONFIG.maxCardsPerOffer) break;
    if (currentValue >= targetValue) break;

    // レアリティを決定（価値に基づく）
    const remainingValue = targetValue - currentValue;
    let selectedRarity = 'C';

    for (let i = RARITY_ORDER.length - 1; i >= 0; i--) {
      const rarity = RARITY_ORDER[i];
      const cardValue = getCardValue(card, rarity, getMarketPrice);
      if (cardValue <= remainingValue && cardValue > 0) {
        selectedRarity = rarity;
        break;
      }
    }

    const cardValue = getCardValue(card, selectedRarity, getMarketPrice);
    if (cardValue > 0 && cardValue <= remainingValue) {
      const maxCount = Math.min(
        OFFER_CONFIG.maxCardsPerOffer - totalCount,
        Math.floor(remainingValue / cardValue)
      );
      const count = Math.max(1, Math.min(maxCount, Math.floor(Math.random() * 3) + 1));

      selected.push({
        cardId: card.id,
        rarity: selectedRarity,
        count,
      });
      currentValue += cardValue * count;
      totalCount += count;
    }
  }

  return selected;
};

// ========================================
// オファー生成
// ========================================

/**
 * 単一のオファーを生成
 * @param {Array} collection - プレイヤーのコレクション
 * @param {Array} allCards - 全カードデータ
 * @param {Function} getMarketPrice - 市場価格取得関数
 * @param {string} offerType - オファータイプ
 * @returns {Object | null}
 */
export const generateSingleOffer = (collection, allCards, getMarketPrice, offerType) => {
  // 渡すカードの生成方式を決定
  const giveType = drawFromRates(GIVE_TYPE_RATES);

  // 基準価値を決定（コレクションの平均価値ベース）
  const avgValue = collection.reduce((sum, item) => {
    const card = allCards.find(c => c.id === item.cardId);
    if (!card) return sum;
    return sum + getCardValue(card, item.rarity, getMarketPrice) * (item.quantity || 1);
  }, 0) / Math.max(1, collection.reduce((sum, item) => sum + (item.quantity || 1), 0));

  const baseValue = Math.max(100, avgValue * (0.5 + Math.random()));

  // 価値比率を決定
  const ratioRange = VALUE_RATIO_RANGES[offerType] || VALUE_RATIO_RANGES.equal;
  const valueRatio = randomInRange(ratioRange.min, ratioRange.max);

  // 渡すカードの目標価値
  const giveTargetValue = baseValue;

  let give = null;
  let giveCondition = null;

  if (giveType === 'specific') {
    give = generateSpecificGive(collection, allCards, giveTargetValue, getMarketPrice);
  } else {
    const conditionResult = generateConditionGive(collection, allCards, giveTargetValue, getMarketPrice);
    if (conditionResult) {
      giveCondition = conditionResult.condition;
      giveCondition.count = conditionResult.count;
    }
  }

  if (!give && !giveCondition) return null;

  // 渡すカードの実際の価値を計算
  let actualGiveValue;
  if (give) {
    actualGiveValue = calculateOfferValue(give, allCards, getMarketPrice);
  } else {
    // 条件指定の場合は平均価値で推定
    const matchingCards = getMatchingCards(collection, allCards, giveCondition);
    const avgMatchValue = matchingCards.reduce((sum, item) => {
      return sum + getCardValue(item.card, item.rarity, getMarketPrice);
    }, 0) / Math.max(1, matchingCards.length);
    actualGiveValue = avgMatchValue * giveCondition.count;
  }

  // もらうカードの目標価値
  const receiveTargetValue = actualGiveValue * valueRatio;

  // 属性変換の場合、元の属性を取得
  let excludeAttribute = null;
  if (offerType === 'attribute' && give && give.length > 0) {
    const firstCard = allCards.find(c => c.id === give[0].cardId);
    excludeAttribute = firstCard?.attribute;
  }

  // もらうカードを生成
  const receive = generateReceiveCards(allCards, receiveTargetValue, getMarketPrice, {
    offerType,
    excludeAttribute,
  });

  if (receive.length === 0) return null;

  // ギャンブルの場合は結果を隠す
  const isGamble = offerType === 'gamble';
  let actualReceive = null;
  if (isGamble) {
    // ギャンブル結果を決定
    const gambleResult = drawFromRates(
      Object.fromEntries(Object.entries(GAMBLE_RESULTS).map(([k, v]) => [k, v.rate]))
    );
    const resultConfig = GAMBLE_RESULTS[gambleResult];
    const gambleRatio = randomInRange(resultConfig.ratio.min, resultConfig.ratio.max);

    actualReceive = generateReceiveCards(allCards, actualGiveValue * gambleRatio, getMarketPrice, {});
    if (actualReceive.length === 0) {
      actualReceive = receive; // フォールバック
    }
  }

  return {
    id: generateOfferId(),
    type: offerType,
    giveType: give ? 'specific' : 'condition',
    give: give || null,
    giveCondition: give ? null : giveCondition,
    receive: isGamble ? null : receive,
    receiveHidden: isGamble,
    actualReceive: isGamble ? actualReceive : null,
    completed: false,
    // 価値情報（UI表示用）
    estimatedGiveValue: Math.round(actualGiveValue),
    estimatedReceiveValue: Math.round(isGamble ? actualGiveValue : calculateOfferValue(receive, allCards, getMarketPrice)),
  };
};

/**
 * ゼルヴァンのオファーを生成
 * @param {Array} collection - プレイヤーのコレクション
 * @param {Array} allCards - 全カードデータ
 * @param {number} dayId - 現在の日
 * @param {Function} getMarketPrice - 市場価格取得関数
 * @returns {Object} { dayId, offers }
 */
export const generateZelvanOffers = (collection, allCards, dayId, getMarketPrice) => {
  const offerCount = OFFER_CONFIG.minOffers + Math.floor(
    Math.random() * (OFFER_CONFIG.maxOffers - OFFER_CONFIG.minOffers + 1)
  );

  const offers = [];
  const usedTypes = new Set();

  for (let i = 0; i < offerCount; i++) {
    // オファータイプを決定（重複を避ける傾向）
    let offerType;
    let attempts = 0;
    do {
      offerType = drawFromRates(OFFER_TYPE_RATES);
      attempts++;
    } while (usedTypes.has(offerType) && attempts < 5);

    usedTypes.add(offerType);

    const offer = generateSingleOffer(collection, allCards, getMarketPrice, offerType);
    if (offer) {
      offers.push(offer);
    }
  }

  return {
    dayId,
    offers,
  };
};

// ========================================
// トレード実行
// ========================================

/**
 * トレードを実行（具体的指定）
 * @param {Object} playerData - プレイヤーデータ
 * @param {Object} offer - オファー
 * @param {Object} collectionManager - コレクション管理
 * @returns {{ success: boolean, playerData: Object, receivedCards: Array, message: string }}
 */
export const executeSpecificTrade = (playerData, offer, collectionManager) => {
  let updatedPlayerData = { ...playerData };

  // 先に所持枚数をチェック
  for (const item of offer.give) {
    const owned = updatedPlayerData.collection?.find(
      c => c.cardId === item.cardId && c.rarity === item.rarity
    );
    if (!owned || owned.quantity < item.count) {
      return { success: false, playerData, receivedCards: [], message: 'カードが不足しています' };
    }
  }

  // 渡すカードを削除
  for (const item of offer.give) {
    for (let i = 0; i < item.count; i++) {
      const result = collectionManager.removeCard(updatedPlayerData, item.cardId, item.rarity);
      updatedPlayerData = { ...result };
    }
  }

  // もらうカードを追加
  const receivedCards = offer.receiveHidden ? offer.actualReceive : offer.receive;
  for (const item of receivedCards) {
    for (let i = 0; i < (item.count || 1); i++) {
      const result = collectionManager.addCard(updatedPlayerData, item.cardId, item.rarity);
      updatedPlayerData = { ...result };
    }
  }

  return {
    success: true,
    playerData: updatedPlayerData,
    receivedCards,
    message: 'トレード成立！',
  };
};

/**
 * トレードを実行（条件指定）
 * @param {Object} playerData - プレイヤーデータ
 * @param {Object} offer - オファー
 * @param {Array} selectedCards - 選択されたカード [{ cardId, rarity }]
 * @param {Object} collectionManager - コレクション管理
 * @returns {{ success: boolean, playerData: Object, receivedCards: Array, message: string }}
 */
export const executeConditionTrade = (playerData, offer, selectedCards, collectionManager) => {
  // 選択枚数チェック
  if (selectedCards.length !== offer.giveCondition.count) {
    return {
      success: false,
      playerData,
      receivedCards: [],
      message: `${offer.giveCondition.count}枚選択してください`,
    };
  }

  let updatedPlayerData = { ...playerData };

  // 先に所持枚数をチェック
  for (const item of selectedCards) {
    const owned = updatedPlayerData.collection?.find(
      c => c.cardId === item.cardId && c.rarity === item.rarity
    );
    if (!owned || owned.quantity < 1) {
      return { success: false, playerData, receivedCards: [], message: 'カードが不足しています' };
    }
  }

  // 選択されたカードを削除
  for (const item of selectedCards) {
    const result = collectionManager.removeCard(updatedPlayerData, item.cardId, item.rarity);
    updatedPlayerData = { ...result };
  }

  // もらうカードを追加
  const receivedCards = offer.receiveHidden ? offer.actualReceive : offer.receive;
  for (const item of receivedCards) {
    for (let i = 0; i < (item.count || 1); i++) {
      const result = collectionManager.addCard(updatedPlayerData, item.cardId, item.rarity);
      updatedPlayerData = { ...result };
    }
  }

  return {
    success: true,
    playerData: updatedPlayerData,
    receivedCards,
    message: 'トレード成立！',
  };
};

/**
 * トレードオファーを実行
 * @param {Object} playerData - プレイヤーデータ
 * @param {string} offerId - オファーID
 * @param {Array} selectedCards - 条件指定の場合の選択カード（省略可）
 * @param {Object} collectionManager - コレクション管理
 * @param {Array} allCards - 全カードデータ
 * @returns {{ success: boolean, playerData: Object, receivedCards: Array, message: string }}
 */
export const executeTradeOffer = (playerData, offerId, selectedCards, collectionManager, allCards) => {
  const tradeData = playerData.tradeData || {};
  const offers = tradeData.zelvanOffers?.offers || [];
  const offer = offers.find(o => o.id === offerId);

  if (!offer) {
    return { success: false, playerData, receivedCards: [], message: 'オファーが見つかりません' };
  }

  if (offer.completed) {
    return { success: false, playerData, receivedCards: [], message: 'このオファーは既に成立しています' };
  }

  let result;
  if (offer.giveType === 'specific') {
    result = executeSpecificTrade(playerData, offer, collectionManager);
  } else {
    // 条件指定の場合、選択されたカードが条件にマッチするかチェック
    for (const item of selectedCards) {
      const card = allCards.find(c => c.id === item.cardId);
      if (!card || !checkConditionMatch(card, item.rarity, offer.giveCondition)) {
        return { success: false, playerData, receivedCards: [], message: '選択したカードが条件に合いません' };
      }
    }
    result = executeConditionTrade(playerData, offer, selectedCards, collectionManager);
  }

  if (result.success) {
    // オファーを完了済みにマーク
    const updatedOffers = offers.map(o =>
      o.id === offerId ? { ...o, completed: true } : o
    );

    // 統計を更新
    const receiveValue = result.receivedCards.reduce((sum, item) => sum + (item.count || 1), 0);
    const stats = { ...(tradeData.stats || {}) };
    stats.totalTrades = (stats.totalTrades || 0) + 1;

    if (offer.type === 'profitable') {
      stats.profitableTrades = (stats.profitableTrades || 0) + 1;
    } else if (offer.type === 'loss') {
      stats.lossTrades = (stats.lossTrades || 0) + 1;
    }

    if (offer.type === 'gamble') {
      const giveValue = offer.estimatedGiveValue || 0;
      const actualReceiveValue = offer.actualReceive?.reduce((sum, item) => sum + (item.count || 1), 0) || 0;
      if (actualReceiveValue > giveValue) {
        stats.gambleWins = (stats.gambleWins || 0) + 1;
      } else {
        stats.gambleLosses = (stats.gambleLosses || 0) + 1;
      }
    }

    result.playerData = {
      ...result.playerData,
      tradeData: {
        ...tradeData,
        zelvanOffers: {
          ...tradeData.zelvanOffers,
          offers: updatedOffers,
        },
        stats,
      },
    };
  }

  return result;
};

// ========================================
// 予約システム
// ========================================

/**
 * オファーを予約
 * @param {Object} playerData - プレイヤーデータ
 * @param {string} offerId - オファーID
 * @param {number} dayId - 現在の日
 * @param {Array} allCards - 全カードデータ
 * @param {Function} getMarketPrice - 市場価格取得関数
 * @returns {{ success: boolean, playerData: Object, message: string }}
 */
export const reserveOffer = (playerData, offerId, dayId, allCards, getMarketPrice) => {
  const tradeData = playerData.tradeData || {};
  const offers = tradeData.zelvanOffers?.offers || [];
  const offer = offers.find(o => o.id === offerId);

  if (!offer) {
    return { success: false, playerData, message: 'オファーが見つかりません' };
  }

  if (offer.completed) {
    return { success: false, playerData, message: 'このオファーは既に成立しています' };
  }

  // 既存の予約があれば警告
  if (tradeData.reservedOffer) {
    return { success: false, playerData, message: '既に予約があります。先にキャンセルしてください' };
  }

  // 手付金を計算
  const receiveCards = offer.receiveHidden ? offer.actualReceive : offer.receive;
  const receiveValue = calculateOfferValue(receiveCards || [], allCards, getMarketPrice);
  const deposit = Math.ceil(receiveValue * RESERVATION_CONFIG.depositRate);

  if (playerData.gold < deposit) {
    return { success: false, playerData, message: `手付金が足りません（${deposit}G必要）` };
  }

  // 予約を作成
  const newReservation = {
    offer: { ...offer },
    deposit,
    reservedAt: dayId,
    expiresAt: dayId + RESERVATION_CONFIG.maxDuration,
  };

  // オファーリストから削除
  const updatedOffers = offers.filter(o => o.id !== offerId);

  // 統計更新
  const stats = { ...(tradeData.stats || {}) };
  stats.reservationsMade = (stats.reservationsMade || 0) + 1;

  return {
    success: true,
    playerData: {
      ...playerData,
      gold: playerData.gold - deposit,
      tradeData: {
        ...tradeData,
        zelvanOffers: {
          ...tradeData.zelvanOffers,
          offers: updatedOffers,
        },
        reservedOffer: newReservation,
        stats,
      },
    },
    message: `オファーを予約しました（手付金: ${deposit}G）`,
  };
};

/**
 * 予約をキャンセル
 * @param {Object} playerData - プレイヤーデータ
 * @returns {{ success: boolean, playerData: Object, message: string }}
 */
export const cancelReservation = (playerData) => {
  const tradeData = playerData.tradeData || {};

  if (!tradeData.reservedOffer) {
    return { success: false, playerData, message: '予約がありません' };
  }

  // 手付金は没収
  const stats = { ...(tradeData.stats || {}) };
  stats.reservationsCancelled = (stats.reservationsCancelled || 0) + 1;

  return {
    success: true,
    playerData: {
      ...playerData,
      tradeData: {
        ...tradeData,
        reservedOffer: null,
        stats,
      },
    },
    message: '予約をキャンセルしました（手付金は没収されます）',
  };
};

/**
 * 予約の期限をチェック
 * @param {Object} playerData - プレイヤーデータ
 * @param {number} dayId - 現在の日
 * @returns {{ expired: boolean, playerData: Object }}
 */
export const checkReservationExpiry = (playerData, dayId) => {
  const tradeData = playerData.tradeData || {};

  if (!tradeData.reservedOffer) {
    return { expired: false, playerData };
  }

  if (dayId > tradeData.reservedOffer.expiresAt) {
    // 期限切れ - 手付金没収
    const stats = { ...(tradeData.stats || {}) };
    stats.reservationsCancelled = (stats.reservationsCancelled || 0) + 1;

    return {
      expired: true,
      playerData: {
        ...playerData,
        tradeData: {
          ...tradeData,
          reservedOffer: null,
          stats,
        },
      },
    };
  }

  return { expired: false, playerData };
};

/**
 * 予約オファーを実行
 * @param {Object} playerData - プレイヤーデータ
 * @param {Array} selectedCards - 条件指定の場合の選択カード
 * @param {Object} collectionManager - コレクション管理
 * @param {Array} allCards - 全カードデータ
 * @returns {{ success: boolean, playerData: Object, receivedCards: Array, message: string }}
 */
export const executeReservedOffer = (playerData, selectedCards, collectionManager, allCards) => {
  const tradeData = playerData.tradeData || {};

  if (!tradeData.reservedOffer) {
    return { success: false, playerData, receivedCards: [], message: '予約がありません' };
  }

  const offer = tradeData.reservedOffer.offer;

  let result;
  if (offer.giveType === 'specific') {
    result = executeSpecificTrade(playerData, offer, collectionManager);
  } else {
    for (const item of selectedCards) {
      const card = allCards.find(c => c.id === item.cardId);
      if (!card || !checkConditionMatch(card, item.rarity, offer.giveCondition)) {
        return { success: false, playerData, receivedCards: [], message: '選択したカードが条件に合いません' };
      }
    }
    result = executeConditionTrade(playerData, offer, selectedCards, collectionManager);
  }

  if (result.success) {
    // 予約をクリア
    const stats = { ...(tradeData.stats || {}) };
    stats.totalTrades = (stats.totalTrades || 0) + 1;
    stats.reservationsCompleted = (stats.reservationsCompleted || 0) + 1;

    result.playerData = {
      ...result.playerData,
      tradeData: {
        ...result.playerData.tradeData,
        reservedOffer: null,
        stats,
      },
    };
  }

  return result;
};

// ========================================
// 初期トレードデータ作成
// ========================================

/**
 * 初期トレードデータを作成
 * @returns {Object}
 */
export const createInitialTradeData = () => ({
  zelvanOffers: {
    dayId: -1,
    offers: [],
  },
  reservedOffer: null,
  requestHistory: {
    dayId: -1,
    used: {},
  },
  stats: {
    totalTrades: 0,
    profitableTrades: 0,
    lossTrades: 0,
    totalValueGained: 0,
    totalValueLost: 0,
    gambleWins: 0,
    gambleLosses: 0,
    reservationsMade: 0,
    reservationsCompleted: 0,
    reservationsCancelled: 0,
  },
  upgradeHistory: [],
  settings: {
    displayMode: 'beginner',
  },
});

// ========================================
// エクスポート
// ========================================

export default {
  // 価値計算
  getCardValue,
  calculateOfferValue,
  // 条件マッチング
  checkConditionMatch,
  getMatchingCards,
  countMatchingCards,
  // オファー生成
  generateZelvanOffers,
  generateSingleOffer,
  // トレード実行
  executeTradeOffer,
  executeSpecificTrade,
  executeConditionTrade,
  // 予約システム
  reserveOffer,
  cancelReservation,
  checkReservationExpiry,
  executeReservedOffer,
  // 初期化
  createInitialTradeData,
};
